import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Area, AreaSubType, getParentEdgeTypeId, Place } from '@iotv/datamodel-core';
import { Alert, Button, Card, CardContent, CardHeader, MenuItem, Radio, Select, Stack, Tooltip } from '@mui/material';
import { kml } from '@tmcw/togeojson';
import { DOMParser, MIME_TYPE } from '@xmldom/xmldom';
import { useMemo, useState } from 'react';
import { ConsumerDao } from '../../../data/ConsumerDAO';
import { getVobFromGeoJson } from '../../../helpers/geojsonHelpers';
import { useSingleClickEditing } from '../../../hooks/DataGrid';
import { Severity } from '../../../types';
import { DataGridRadioColumn } from '../../../util/DataGrid/DataGridHelpers';
import FileInputButton from '../../common/FileInputButton';
import { StripedDataGrid } from '../../factories/DataGrid';
import { readableTypeName } from '@iotv/core-services';
const debug = process.env.REACT_APP_DEBUG && false;
/** Imports Areas and Places from KML data */
export const KMLImportTab = (props) => {
    const { contextObject, childAreas, childPlaces, userFunctions, viewOnly } = props;
    const [areas, setAreas] = useState([]);
    const [fastSubtype, setFastSubtype] = useState(AreaSubType[0]);
    const [places, setPlaces] = useState([]);
    /** Maps area sk: child area sk so the user can see / configure if imported areas will replace areas in the system
     * Manually configuring might be necessary since matching is purely on names.
     * If replaced, the child areas name, points, description, subtype will be replaced. No link transplants should be necessary **/
    const [areaReplacementMap, setAreaReplacementMap] = useState({});
    const [placeReplacementMap, setPlaceReplacementMap] = useState({});
    const [processingKML, setProcessingKML] = useState(false);
    const [processingImport, setProcessingImport] = useState(false);
    /** Takes kml data and turns it into areas, places for edit and import */
    const uploadKML = (e) => {
        setProcessingKML(true);
        const files = e.target.files;
        if (!files?.[0]) {
            return;
        }
        const kmlReader = new FileReader();
        kmlReader.addEventListener('loadend', (e_load) => {
            if (!e_load?.target?.result) {
                console.error('KML Import Tab', 'Error loading KML');
                return;
            }
            if (typeof e_load.target.result !== 'string') {
                console.error('KML Import Tab', 'File loaded as array buffer, aborting');
                return;
            }
            const kml_str = e_load.target.result;
            //console.log(kml_str)
            const kml_xml = new DOMParser().parseFromString(kml_str, MIME_TYPE.XML_TEXT);
            const kml_geojson = kml(kml_xml);
            // Turn geojson features into areas/places
            const vobs = kml_geojson.features.map(getVobFromGeoJson).filter((v) => !!v);
            //console.log(vobs)
            /** Load Places **/
            const newPlaces = vobs.filter((v) => v.type === 'Place');
            const placeReplaceMap = {};
            for (const p of newPlaces) {
                placeReplaceMap[p.sk] = 'null'; // Default
                const correspondingPlace = childPlaces?.find((ca) => ca.name === p.name);
                if (correspondingPlace) {
                    placeReplaceMap[p.sk] = correspondingPlace.sk;
                    p.description = correspondingPlace.description;
                }
            }
            setPlaceReplacementMap(placeReplaceMap);
            setPlaces(newPlaces);
            /** Load Areas **/
            const newAreas = vobs.filter((v) => v.type === 'Area');
            // Set up areaReplacementMap
            const areaReplaceMap = {};
            for (const a of newAreas) {
                areaReplaceMap[a.sk] = 'null'; // Default
                const correspondingArea = childAreas?.find((ca) => ca.name === a.name) ?? (contextObject.name === a.name ? contextObject : undefined);
                if (correspondingArea) {
                    areaReplaceMap[a.sk] = correspondingArea.sk;
                    if (correspondingArea.type === 'Area') {
                        a.subType = correspondingArea.subType;
                    }
                    a.description = correspondingArea.description;
                }
            }
            setAreaReplacementMap(areaReplaceMap);
            setAreas(newAreas);
            setProcessingKML(false);
        });
        kmlReader.readAsText(files[0]);
    };
    const importAreasAndPlaces = () => {
        const selectedAreaIds = 'ids' in selectedAreasModel ? Array.from(selectedAreasModel.ids) : selectedAreasModel;
        const selectedPlaceIds = 'ids' in selectedPlacesModel ? Array.from(selectedPlacesModel.ids) : selectedPlacesModel;
        const selectedAreas = areas.filter((a) => selectedAreaIds.includes(a.id));
        const selectedPlaces = places.filter((a) => selectedPlaceIds.includes(a.id));
        debug && console.log('Importing', selectedAreas, selectedPlaces);
        setProcessingImport(true);
        const areaLinkPromises = selectedAreas.map((a) => new Promise(async (res, rej) => {
            const areaToReplace = areaReplacementMap[a.sk] === contextObject.sk ? contextObject : childAreas?.find((ca) => ca.sk === areaReplacementMap[a.sk]);
            if (areaToReplace) {
                console.log('Replacing', areaToReplace);
                const replaced = { ...areaToReplace, ...a };
                replaced.pk = areaToReplace.pk;
                replaced.sk = areaToReplace.sk;
                replaced.id = areaToReplace.id;
                replaced.name = areaToReplace.name; // Retain original name ??
                replaced.type = areaToReplace.type; // Area to replace might be a property
                if (replaced.type === 'Property') {
                    delete replaced.subType;
                }
                else {
                    replaced.subType = areaToReplace.subType;
                }
                const replace_res = await ConsumerDao.save(replaced);
                if (replace_res.err || !replace_res.data) {
                    rej(replace_res.err?.message ?? 'No data on replace area');
                    return;
                }
                res({ savedArea: replaced, newEdge: undefined });
            }
            else {
                const save_res = await ConsumerDao.save(a);
                if (save_res.err || !save_res.data) {
                    rej(save_res.err?.message ?? 'No data on save new area');
                    return;
                }
                const link_res = await ConsumerDao.link(contextObject, a, getParentEdgeTypeId(a.type, contextObject.type));
                if (link_res.err || !link_res.data) {
                    rej(link_res.err?.message ?? 'No data on link new area');
                    return;
                }
                res({ savedArea: a, newEdge: link_res.data[2] });
            }
        }));
        const placeLinkPromises = selectedPlaces.map((p) => new Promise(async (res, rej) => {
            const placeToReplace = childPlaces?.find((cp) => cp.sk === placeReplacementMap[p.sk]);
            if (placeToReplace) {
                const replaced = { ...placeToReplace, ...p };
                replaced.pk = placeToReplace.pk;
                replaced.sk = placeToReplace.sk;
                replaced.id = placeToReplace.id;
                replaced.name = placeToReplace.name;
                const replace_res = await ConsumerDao.save(p);
                if (replace_res.err || !replace_res.data) {
                    rej(replace_res.err?.message ?? 'No data on replace place');
                    return;
                }
                res({ savedPlace: replaced, newEdge: undefined });
            }
            else {
                const save_res = await ConsumerDao.save(p);
                if (save_res.err || !save_res.data) {
                    rej(save_res.err?.message ?? 'No data on save new place');
                    return;
                }
                const link_res = await ConsumerDao.link(contextObject, p, getParentEdgeTypeId(p.type, contextObject.type));
                if (link_res.err || !link_res.data) {
                    rej(link_res.err?.message ?? 'No data on link new place');
                    return;
                }
                res({ savedPlace: p, newEdge: link_res.data[2] });
            }
        }));
        Promise.allSettled([...areaLinkPromises, ...placeLinkPromises]).then((reses) => {
            console.log(reses);
            const successfulReses = reses.filter((r) => r.status === 'fulfilled').map((r) => r.value);
            const failedReses = reses.filter((r) => r.status === 'rejected');
            let newStateData = [];
            for (const res of successfulReses) {
                newStateData.push(res.savedArea);
                newStateData.push(res.savedPlace);
                newStateData.push(res.newEdge);
            }
            newStateData = newStateData.filter((d) => !!d);
            userFunctions.addObjectsToState(newStateData);
            setProcessingImport(false);
            if (successfulReses.length > 0 && failedReses.length > 0) {
                userFunctions.setUserMessage({
                    id: 'savedKMLImportMessage',
                    content: 'Some data successfully imported, but some failed.',
                    severity: Severity.warning,
                    label: 'Imported KML',
                    type: 'SnackBar',
                });
            }
            else if (successfulReses.length === 0 && failedReses.length > 0) {
                userFunctions.setUserMessage({
                    id: 'savedKMLImportMessage',
                    content: 'There was an error importing KML data',
                    severity: Severity.error,
                    label: 'Imported KML',
                    type: 'SnackBar',
                });
            }
            else if (successfulReses.length > 0 && failedReses.length === 0) {
                userFunctions.setUserMessage({
                    id: 'savedKMLImportMessage',
                    content: 'Successfully imported KML data',
                    severity: Severity.success,
                    label: 'Imported KML',
                    type: 'SnackBar',
                });
            }
        });
    };
    const dataGridProps = {
        //loading,
        getRowClassName: (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'),
        initialState: {
            sorting: {
                sortModel: [{ field: 'name', sort: 'asc' }],
            },
        },
        density: 'compact',
        checkboxSelection: true,
        disableRowSelectionExcludeModel: true,
    };
    /** AREA SPECIFIC */
    const updateBlankSubtypes = (newSubtype) => {
        setAreas((old) => {
            const newAreas = areas.map((a) => new Area({ ...a, subType: a.subType ?? newSubtype }));
            return newAreas;
        });
    };
    const updateAllSubtypes = (newSubtype) => {
        setAreas((old) => {
            const newAreas = areas.map((a) => new Area({ ...a, subType: newSubtype }));
            return newAreas;
        });
    };
    const designateAreaAsMapArea = (area) => {
        setAreaReplacementMap((old) => {
            const clone = { ...old };
            const currentMapAreaEntry = Object.entries(old).find(([areaSk, childAreaSk]) => childAreaSk === contextObject.sk);
            if (currentMapAreaEntry) {
                const currentMapAreaSk = currentMapAreaEntry[0];
                clone[currentMapAreaSk] = 'null';
                // If we clicked the radio that is currently selected, go back
                if (currentMapAreaSk === area.sk) {
                    return clone;
                }
            }
            clone[area.sk] = contextObject.sk;
            return clone;
        });
    };
    const removeAreaRow = (params) => {
        console.log(params);
        setAreaReplacementMap((old) => {
            const clone = { ...old };
            delete clone[params.row.sk];
            return clone;
        });
        setAreas((old) => [...old.filter((a) => a.sk !== params.row.sk)]);
    };
    const singleClickAreaEditing = useSingleClickEditing();
    const [selectedAreasModel, setSelectedAreasModel] = useState({ type: 'include', ids: new Set() });
    const dataGridAreaProps = {
        ...dataGridProps,
        rowSelectionModel: selectedAreasModel,
        onRowSelectionModelChange: setSelectedAreasModel,
        ...singleClickAreaEditing,
        //setRowSelectionModel,
        columns: [
            DataGridRadioColumn,
            {
                field: 'name',
                headerName: 'Area Name',
                editable: viewOnly,
                valueGetter: (p, r) => (areaReplacementMap[r.sk] !== 'null' ? '' : r.name),
                flex: 1,
            },
            {
                field: 'replacesSk',
                headerName: 'Replacing',
                valueGetter: (p, r) => areaReplacementMap[r.sk],
                // valueSetter: (newValue, row, columnInfo, apiRef) => {
                //   if (newValue !== areaReplacementMap[row.sk]) {
                //     console.log('Setter', newValue, row, columnInfo)
                //
                //     setAreaReplacementMap((old) => ({ ...old, [row.sk]: newValue }))
                //   }
                //   return row
                // },
                editable: viewOnly,
                type: 'singleSelect',
                valueOptions: ['null', ...(childAreas?.map((a) => a.sk) ?? []), contextObject.sk],
                getOptionLabel: (option) => {
                    if (option === 'null')
                        return '-';
                    if (option === contextObject.sk)
                        return contextObject.name ?? `Unnamed ${readableTypeName(contextObject.type)}`;
                    return childAreas?.find((ca) => ca.sk === option)?.name ?? 'ERROR';
                },
                flex: 1,
            },
            {
                field: 'description',
                headerName: 'Description',
                editable: true,
                flex: 1,
            },
            {
                field: 'subType',
                headerName: 'Subtype',
                type: 'singleSelect',
                valueOptions: AreaSubType,
                valueGetter: (p, r) => {
                    if (areaReplacementMap[r.sk] === contextObject.sk) {
                        return contextObject.type === 'Property' ? 'N/A' : contextObject.subType;
                    }
                    return r.subType;
                },
                editable: true,
                flex: 1,
            },
            {
                field: 'useAsMapArea',
                headerName: 'Use as Map Area',
                valueGetter: (p, r) => areaReplacementMap[r.sk] === contextObject.sk,
                renderCell: (p) => _jsx(Radio, { checked: p.value, onClick: (e) => designateAreaAsMapArea(p.row) }),
                //editable: true,
                flex: 0.6,
            },
        ],
        rows: areas,
        processRowUpdate: (newArea, oldArea, c) => {
            //console.log(newArea, oldArea)
            setAreas((old) => [...old.filter((a) => a.sk !== newArea.sk), new Area(newArea)]);
            //updateAreaKey('name', newArea.name, newArea)
            //console.log(a, b, c)
            return newArea;
        },
        isCellEditable: (p) => {
            if (areaReplacementMap[p.row.sk] === contextObject.sk) {
                if (p.colDef.field === 'subType') {
                    return false;
                }
                else {
                    return p.colDef.editable ?? false;
                }
            }
            else {
                return p.colDef.editable ?? false;
            }
        },
    };
    /** PLACES **/
    const singleClickPlaceEditing = useSingleClickEditing();
    const [selectedPlacesModel, setSelectedPlacesModel] = useState({ type: 'include', ids: new Set() });
    const dataGridPlaceProps = {
        ...dataGridProps,
        rowSelectionModel: selectedPlacesModel,
        onRowSelectionModelChange: setSelectedPlacesModel,
        ...singleClickPlaceEditing,
        columns: [
            DataGridRadioColumn,
            {
                field: 'name',
                headerName: 'Place Name',
                editable: true,
                valueGetter: (p, r) => (placeReplacementMap[r.sk] !== 'null' ? '' : r.name),
                flex: 1,
            },
            {
                field: 'replacesSk',
                headerName: 'Replacing',
                valueGetter: (p, r) => placeReplacementMap[r.sk],
                valueSetter: (newValue, row, columnInfo, apiRef) => {
                    if (newValue !== placeReplacementMap[row.sk]) {
                        setPlaceReplacementMap((old) => ({ ...old, [row.sk]: newValue }));
                    }
                    return row;
                },
                editable: true,
                type: 'singleSelect',
                valueOptions: ['null', ...(childPlaces?.map((a) => a.sk) ?? [])],
                getOptionLabel: (option) => {
                    if (option === 'null')
                        return '-';
                    if (option === contextObject.sk)
                        return contextObject.name ?? `Unnamed ${readableTypeName(contextObject.type)}`;
                    return childPlaces?.find((cp) => cp.sk === option)?.name ?? 'ERROR';
                },
                flex: 1,
            },
            {
                field: 'description',
                headerName: 'Description',
                editable: true,
                flex: 1,
            },
        ],
        rows: places,
        processRowUpdate: (newPlace, oldArea, c) => {
            //console.log(newArea, oldArea)
            setPlaces((old) => [...old.filter((p) => p.sk !== newPlace.sk), new Place(newPlace)]);
            //updateAreaKey('name', newArea.name, newArea)
            //console.log(a, b, c)
            return newPlace;
        },
    };
    const removePlaceRow = (params) => {
        setPlaceReplacementMap((old) => {
            const clone = { ...old };
            delete clone[params.row.sk];
            return clone;
        });
        setPlaces((old) => [...old.filter((p) => p.sk !== params.row.sk)]);
    };
    /** VALIDATION **/
    const validation = useMemo(() => {
        const selectedAreaIds = 'ids' in selectedAreasModel ? Array.from(selectedAreasModel.ids) : selectedAreasModel;
        const selectedPlaceIds = 'ids' in selectedPlacesModel ? Array.from(selectedPlacesModel.ids) : selectedPlacesModel;
        const selectedAreas = areas.filter((a) => selectedAreaIds.includes(a.id));
        const selectedPlaces = places.filter((a) => selectedPlaceIds.includes(a.id));
        if (selectedAreas.length + selectedPlaces.length === 0) {
            return { valid: false, reason: 'Please select at least one area or place to import' };
        }
        // Do all areas have a subtype? (exception being if an area is going to replace the context object and the context object is a property OR that context object has a subtype)
        const areasWithoutSubtype = selectedAreas.filter((a) => a.subType === undefined);
        if (areasWithoutSubtype.length > 1) {
            return { valid: false, reason: 'One or more areas do not have a subtype' };
        }
        else if (areasWithoutSubtype.length === 1) {
            const areaLackingSubtype = areasWithoutSubtype[0];
            if (areaReplacementMap[areaLackingSubtype.sk] === contextObject.sk) {
                if (contextObject.type === 'Property') {
                    // Fine - context object is property so area does not need a sub type
                }
                else if (contextObject.subType !== undefined) {
                    // Also Fine - context object is area and has subType to inherit from
                }
                else {
                    return { valid: false, reason: 'One or more areas do not have a subtype' };
                }
            }
            else {
                return { valid: false, reason: 'One or more areas do not have a subtype' };
            }
        }
        for (const area of selectedAreas) {
            if (area.name === '' && areaReplacementMap[area.sk] !== contextObject.sk) {
                return { valid: false, reason: 'One or more areas do not have a name' };
            }
        }
        for (const place of selectedPlaces) {
            if (place.name === '') {
                return { valid: false, reason: 'One or more places do not have a name' };
            }
        }
        return { valid: true, reason: null };
    }, [areas, places, areaReplacementMap, selectedAreasModel, selectedPlacesModel]);
    return (_jsxs(Stack, { gap: 1, children: [_jsx(FileInputButton, { buttonProps: {
                    variant: 'contained',
                    loading: processingKML,
                }, inputProps: {
                    onChange: (e) => uploadKML(e),
                }, children: "Upload KML" }), _jsx(Button, { variant: "contained", onClick: importAreasAndPlaces, disabled: !validation.valid || areas.length + places.length === 0 || viewOnly, loading: processingImport, children: "Import" }), !validation.valid && validation.reason !== null && _jsx(Alert, { severity: "error", children: validation.reason }), _jsxs(Card, { children: [_jsx(CardHeader, { title: "Areas" }), _jsx(CardContent, { children: _jsxs(Stack, { gap: 1, children: [_jsxs(Stack, { direction: "row", gap: 1, children: [_jsx(Select, { sx: { minWidth: 200 }, size: "small", value: fastSubtype, onChange: (e) => setFastSubtype(e.target.value), disabled: areas.length === 0, children: AreaSubType.map((st) => (_jsx(MenuItem, { value: st, children: st }, st))) }), _jsx(Tooltip, { title: "Sets the subtype of any area that doesn't already have one", children: _jsx(Button, { onClick: () => updateBlankSubtypes(fastSubtype), variant: "contained", disabled: areas.length === 0 || viewOnly, children: "Set blank Sub Types" }) }), _jsx(Tooltip, { title: "Sets the subtype of every area", children: _jsx(Button, { onClick: () => updateAllSubtypes(fastSubtype), variant: "contained", disabled: areas.length === 0 || viewOnly, children: "Set all Sub Types" }) })] }), _jsx(StripedDataGrid, { ...dataGridAreaProps })] }) })] }), _jsxs(Card, { children: [_jsx(CardHeader, { title: "Places" }), _jsx(CardContent, { children: _jsx(StripedDataGrid, { ...dataGridPlaceProps }) })] })] }));
    //return <Button variant="contained">Upload KML</Button>
};
